import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import axios from 'axios';

const ResponsesManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [responses, setResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [currentComment, setCurrentComment] = useState('');
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [filter, setFilter] = useState({
    questionnaire: 'all',
    dateRange: 'all',
    status: 'all'
  });
  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  useEffect(() => {
    
    axios.get(`${process.env.REACT_APP_API_URL}/questionnaires`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(response => {
        if (response.data.success) {
          setQuestionnaires(response.data.data);
        }
      })
      .catch(error => {
        console.error('Error fetching questionnaires:', error);
      });
      
    axios.get(`${process.env.REACT_APP_API_URL}/admin/responses`)
      .then(response => {
        if (response.data.success) {
          // Process the response data to ensure it has all required fields
          const processedResponses = response.data.data.map(response => {
            // Ensure questionnaire_title exists
            const questionnaire_title = response.questionnaire ? 
              response.questionnaire.title : 
              'Unknown Questionnaire';
            
            // Ensure answers array exists
            const answers = response.answers || [];
            
            // Process each answer to ensure it has the required fields
            const processedAnswers = answers.map(answer => {
              // Extract the answer value from the appropriate field based on the question type
              let answerValue;
              
              if (answer.answer_text !== undefined && answer.answer_text !== null) {
                answerValue = answer.answer_text;
              } else if (answer.answer_options !== undefined && answer.answer_options !== null) {
                // Handle answer_options which might be a JSON string
                if (typeof answer.answer_options === 'string' && 
                    (answer.answer_options.startsWith('[') || answer.answer_options.startsWith('{'))) {
                  try {
                    answerValue = JSON.parse(answer.answer_options);
                  } catch (e) {
                    answerValue = answer.answer_options;
                  }
                } else {
                  answerValue = answer.answer_options;
                }
              } else if (answer.file_path) {
                answerValue = `File: ${answer.file_path}`;
              } else if (answer.value !== undefined) {
                answerValue = answer.value;
              } else if (answer.answer !== undefined) {
                answerValue = answer.answer;
              } else {
                answerValue = 'No response';
              }
              
              return {
                ...answer,
                question_id: answer.question_id || answer.id || 0,
                question_title: answer.question ? answer.question.title : 'Unknown Question',
                answer: answerValue,
                comments: answer.comments || []
              };
            });
            
            return {
              ...response,
              questionnaire_title,
              answers: processedAnswers,
              status: response.status || 'pending',
              submitted_at: response.created_at || response.submitted_at || null
            };
          });
          
          console.log('Processed responses:', processedResponses);
          setResponses(processedResponses);
        }
      })
      .catch(error => {
        console.error('Error fetching responses:', error);
      });

  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewResponse = (response) => {
    setSelectedResponse(response);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
  };

  const handleOpenCommentDialog = (questionId) => {
    setCurrentQuestionId(questionId);
    
    // Find existing comment if any
    const question = selectedResponse.answers.find(a => a.question_id === questionId);
    const existingComment = question.comments.length > 0 ? question.comments[0] : '';
    
    setCurrentComment(existingComment);
    setOpenCommentDialog(true);
  };

  const handleCloseCommentDialog = () => {
    setOpenCommentDialog(false);
    setCurrentComment('');
    setCurrentQuestionId(null);
  };

  const handleSaveComment = () => {
    // In a real application, we would send this to the backend
    const updatedResponses = responses.map(response => {
      if (response.id === selectedResponse.id) {
        const updatedAnswers = response.answers.map(answer => {
          if (answer.question_id === currentQuestionId) {
            // If there's an existing comment, replace it; otherwise add a new one
            const updatedComments = answer.comments.length > 0 
              ? [currentComment, ...answer.comments.slice(1)] 
              : [currentComment];
            
            return { ...answer, comments: updatedComments };
          }
          return answer;
        });
        
        return { 
          ...response, 
          answers: updatedAnswers,
          status: 'reviewed' // Mark as reviewed when a comment is added
        };
      }
      return response;
    });
    
    setResponses(updatedResponses);
    
    // Update the selected response as well
    const updatedSelectedResponse = updatedResponses.find(r => r.id === selectedResponse.id);
    setSelectedResponse(updatedSelectedResponse);
    
    // In a real application:
    /*
    axios.post(`/api/admin/responses/${selectedResponse.id}/comment`, {
      question_id: currentQuestionId,
      comment: currentComment
    })
      .then(response => {
        // Update responses list
      })
      .catch(error => {
        console.error('Error saving comment:', error);
      });
    */
    
    handleCloseCommentDialog();
  };

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
  };

  const handleCloseFilterDialog = () => {
    setOpenFilterDialog(false);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilter({ ...filter, [name]: value });
  };

  const handleApplyFilter = () => {
    // In a real application, we would fetch filtered data from the backend
    // For now, we'll just close the dialog
    handleCloseFilterDialog();
  };

  const handleExportResponses = () => {
    // In a real application, we would generate and download a CSV/Excel file
    alert('Export functionality would download responses as CSV/Excel');
    
    // In a real application:
    /*
    axios.get('/api/admin/responses/export', {
      params: filter,
      responseType: 'blob'
    })
      .then(response => {
        // Create a download link and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'responses.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(error => {
        console.error('Error exporting responses:', error);
      });
    */
  };

  const getFilteredResponses = () => {
    let filtered = [...responses];
    
    if (filter.questionnaire !== 'all') {
      filtered = filtered.filter(r => r.questionnaire_id.toString() === filter.questionnaire);
    }
    
    if (filter.status !== 'all') {
      filtered = filtered.filter(r => r.status === filter.status);
    }
    
    // In a real application, we would also filter by date range
    
    return filtered;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'reviewed':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const filteredResponses = getFilteredResponses();
  const pendingResponses = filteredResponses.filter(r => r.status === 'pending');
  const reviewedResponses = filteredResponses.filter(r => r.status === 'reviewed');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Response Manager
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={handleOpenFilterDialog}
            sx={{ mr: 2 }}
          >
            Filter
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportResponses}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`All (${filteredResponses.length})`} />
          <Tab label={`Pending (${pendingResponses.length})`} />
          <Tab label={`Reviewed (${reviewedResponses.length})`} />
        </Tabs>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Respondent</TableCell>
                <TableCell>Questionnaire</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tabValue === 0 ? filteredResponses : 
                tabValue === 1 ? pendingResponses : 
                reviewedResponses).map((response) => (
                <TableRow key={response.id}>
                  <TableCell>{response.respondent_email}</TableCell>
                  <TableCell>{response.questionnaire_title}</TableCell>
                  <TableCell>{formatDate(response.submitted_at)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={response.status === 'reviewed' ? 'Reviewed' : 'Pending Review'} 
                      color={getStatusColor(response.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewResponse(response)}
                      color="primary"
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(tabValue === 0 ? filteredResponses.length === 0 : 
                tabValue === 1 ? pendingResponses.length === 0 : 
                reviewedResponses.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No responses found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Response Dialog */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedResponse && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Response from {selectedResponse.respondent_email}
                </Typography>
                <Chip 
                  label={selectedResponse.status === 'reviewed' ? 'Reviewed' : 'Pending Review'} 
                  color={getStatusColor(selectedResponse.status)} 
                  size="small" 
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Questionnaire: {selectedResponse.questionnaire_title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Submitted: {formatDate(selectedResponse.submitted_at)}
                </Typography>
              </Box>
              
              <Typography variant="h6" gutterBottom>
                Responses
              </Typography>
              
              {selectedResponse.answers.map((answer) => (
                <Accordion key={answer.question_id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 'medium' }}>
                      {answer.question_title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Response:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        {(() => {
                          // Try to parse JSON if answer is a string that looks like JSON
                          let parsedAnswer = answer.answer;
                          if (typeof parsedAnswer === 'string' && 
                              (parsedAnswer.startsWith('[') || parsedAnswer.startsWith('{'))) {
                            try {
                              parsedAnswer = JSON.parse(parsedAnswer);
                            } catch (e) {
                              // If parsing fails, keep the original string
                              console.log('Failed to parse JSON answer:', e);
                            }
                          }
                          
                          // Display based on the type of answer
                          if (Array.isArray(parsedAnswer)) {
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {parsedAnswer.length > 0 ? (
                                  parsedAnswer.map((item, index) => (
                                    <Chip key={index} label={item} size="small" />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No options selected
                                  </Typography>
                                )}
                              </Box>
                            );
                          } else if (parsedAnswer === null || parsedAnswer === undefined) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                No response provided
                              </Typography>
                            );
                          } else if (typeof parsedAnswer === 'object') {
                            return (
                              <Typography variant="body1">
                                {JSON.stringify(parsedAnswer, null, 2)}
                              </Typography>
                            );
                          } else {
                            return (
                              <Typography variant="body1">{String(parsedAnswer)}</Typography>
                            );
                          }
                        })()}
                      </Paper>
                    </Box>
                    
                    {answer.review_guidelines && (
                      <Box sx={{ mb: 2 }} className="guidelines-section">
                        <Typography variant="subtitle2" gutterBottom>
                          Review Guidelines:
                        </Typography>
                        <Typography variant="body2">
                          {answer.review_guidelines}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box className="comment-section">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">
                          Admin Comments:
                        </Typography>
                        <Button
                          startIcon={<CommentIcon />}
                          size="small"
                          onClick={() => handleOpenCommentDialog(answer.question_id)}
                        >
                          {answer.comments.length > 0 ? 'Edit Comment' : 'Add Comment'}
                        </Button>
                      </Box>
                      
                      {answer.comments.length > 0 ? (
                        <List dense>
                          {answer.comments.map((comment, index) => (
                            <ListItem key={index} disablePadding>
                              <ListItemText primary={comment} />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No comments yet
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseViewDialog}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Comment Dialog */}
      <Dialog 
        open={openCommentDialog} 
        onClose={handleCloseCommentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Comment
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            label="Comment"
            multiline
            rows={4}
            value={currentComment}
            onChange={(e) => setCurrentComment(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="Enter your review comment here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommentDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveComment} 
            variant="contained" 
            startIcon={<SendIcon />}
            disabled={!currentComment.trim()}
          >
            Save Comment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog 
        open={openFilterDialog} 
        onClose={handleCloseFilterDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Filter Responses
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Questionnaire</InputLabel>
              <Select
                name="questionnaire"
                value={filter.questionnaire}
                onChange={handleFilterChange}
                label="Questionnaire"
              >
                <MenuItem value="all">All Questionnaires</MenuItem>
                {questionnaires.map(questionnaire => (
                  <MenuItem key={questionnaire.id} value={questionnaire.id.toString()}>
                    {questionnaire.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending Review</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select
                name="dateRange"
                value={filter.dateRange}
                onChange={handleFilterChange}
                label="Date Range"
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFilterDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleApplyFilter} 
            variant="contained"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResponsesManager;
